import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type AccountType = "marketplace" | "manage";

type ManageAccountState = {
  activeAccount: AccountType;
  lastRoute: {
    marketplace: string;
    manage: string;
  };
};

type ManageAccountContextValue = ManageAccountState & {
  switchAccount: (next: AccountType) => void;
  setLastRoute: (account: AccountType, route: string) => void;
};

const STORAGE_KEY = "droomwork-active-account-v1";
const DEFAULT_STATE: ManageAccountState = {
  activeAccount: "marketplace",
  lastRoute: {
    marketplace: "/dashboard",
    manage: "/manage/onboarding",
  },
};

export const ManageAccountContext = createContext<ManageAccountContextValue>({
  ...DEFAULT_STATE,
  switchAccount: () => {},
  setLastRoute: () => {},
});

function safeReadState(): ManageAccountState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<ManageAccountState>;
    return {
      activeAccount:
        parsed.activeAccount === "manage" ? "manage" : "marketplace",
      lastRoute: {
        marketplace: parsed.lastRoute?.marketplace ?? DEFAULT_STATE.lastRoute.marketplace,
        manage: parsed.lastRoute?.manage ?? DEFAULT_STATE.lastRoute.manage,
      },
    };
  } catch {
    return DEFAULT_STATE;
  }
}

export const ManageAccountProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<ManageAccountState>(() => {
    if (typeof window === "undefined") return DEFAULT_STATE;
    return safeReadState();
  });

  const setLastRoute = useCallback((account: AccountType, route: string) => {
    setState((prev) => ({
      ...prev,
      lastRoute: {
        ...prev.lastRoute,
        [account]: route,
      },
    }));
  }, []);

  const switchAccount = useCallback(
    (next: AccountType) => {
      setState((prev) => {
        const nextRoute = prev.lastRoute[next] ?? (next === "manage" ? "/manage/onboarding" : "/dashboard");
        // Navigate after state update to keep UI consistent
        setTimeout(() => navigate(nextRoute), 0);
        return { ...prev, activeAccount: next };
      });
    },
    [navigate]
  );

  // Keep last route updated based on current location.
  useEffect(() => {
    const path = location.pathname;
    const currentAccount: AccountType = path.startsWith("/manage")
      ? "manage"
      : "marketplace";
    setState((prev) => ({
      ...prev,
      activeAccount: currentAccount,
      lastRoute: {
        ...prev.lastRoute,
        [currentAccount]: path,
      },
    }));
  }, [location.pathname]);

  // Persist.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  const value = useMemo(
    () => ({
      ...state,
      switchAccount,
      setLastRoute,
    }),
    [state, switchAccount, setLastRoute]
  );

  return (
    <ManageAccountContext.Provider value={value}>
      {children}
    </ManageAccountContext.Provider>
  );
};

